import { useState, useEffect } from "react";
import "./TenantSwitcher.css";

const TenantSwitcher = ({ visible, onClose, onSwitch, onAddNew }) => {
  const [tenants, setTenants] = useState([]);
  const [activeTenantCode, setActiveTenantCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [isProvisioned, setIsProvisioned] = useState(false);

  useEffect(() => {
    if (visible && window.electronAPI?.tenant) {
      window.electronAPI.tenant.getAll().then(setTenants);
      window.electronAPI.tenant.getActive().then((t) => {
        if (t) setActiveTenantCode(t.code);
      });
    }
    if (visible && window.electronAPI?.provision) {
      window.electronAPI.provision.isActivated().then(setIsProvisioned);
    }
  }, [visible]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const result = await window.electronAPI.provision.syncTenants();
      const updated = await window.electronAPI.tenant.getAll();
      setTenants(updated);

      // Clear app cache so landing page picks up fresh apps
      if (window.electronAPI?.apps) {
        await window.electronAPI.apps.clearCache();
      }

      // Notify landing page to refresh apps & plugins
      window.dispatchEvent(new Event("cw:org-synced"));

      if (result?.success) {
        setSyncMessage({ type: "success", text: `Synced ${result.count || updated.length} organization(s)` });
      } else {
        const url = result?.adminUrl ? ` (${result.adminUrl})` : "";
        setSyncMessage({ type: "error", text: (result?.error || "Sync returned no data") + url });
      }
    } catch (err) {
      console.error("[TenantSwitcher] Sync failed:", err);
      setSyncMessage({ type: "error", text: "Sync failed — check network connection" });
    }
    setSyncing(false);
  };

  if (!visible) return null;

  const handleSwitch = async (code) => {
    if (code === activeTenantCode) {
      onClose();
      return;
    }

    setLoading(true);

    try {
      // Clear auth cookies before switching — delete with multiple domain variants
      const cookiesToDelete = document.cookie.split(";").map((c) => c.trim());
      const hostname = window.location.hostname;
      const domainVariants = ["", hostname];
      const parts = hostname.split(".");
      if (parts.length > 2) {
        domainVariants.push("." + parts.slice(-2).join("."));
      }
      for (const cookie of cookiesToDelete) {
        const name = cookie.split("=")[0];
        for (const domain of domainVariants) {
          const domainPart = domain ? `domain=${domain};` : "";
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;${domainPart}`;
        }
      }

      console.debug("[TenantSwitcher] Switching to:", code);
      await window.electronAPI.tenant.switch(code);

      // Clear stale tenant URL overrides before reload
      delete window.__CW_TENANT_URLS__;

      // Destroy socket connection to old tenant's server
      try {
        const { destroySocket } = await import("../../socket");
        destroySocket();
      } catch (e) { /* socket may not be initialized */ }

      // Clear ALL persisted Zustand stores so stale data from old tenant
      // doesn't leak into the new tenant's session after reload.
      const [
        { default: useAuthStore },
        { default: useSessionStore },
        { default: useCartStore },
        { default: usePOSConfigStore },
        { default: useTransactionStore },
        { default: useRestaurantStore },
      ] = await Promise.all([
        import("../../stores/useAuthStore"),
        import("../../stores/useSessionStore"),
        import("../../stores/useCartStore"),
        import("../../stores/usePOSConfigStore"),
        import("../../stores/useTransactionStore"),
        import("../../stores/useRestaurantStore"),
      ]);

      useAuthStore.getState().logout();
      try { useSessionStore.getState().clearSession(); } catch (e) {}
      try { useCartStore.getState().clearCartData?.() || useCartStore.setState({ cart: null, exchangeCart: null }); } catch (e) {}
      try { useTransactionStore.setState({ selectedReturnType: null, walletPayments: [] }); } catch (e) {}
      try { useRestaurantStore.setState({}); } catch (e) {}
      try { usePOSConfigStore.setState({}); } catch (e) {}

      if (onSwitch) onSwitch(code);
      window.location.href = "/sign-in";
    } catch (err) {
      console.error("[TenantSwitcher] Switch failed:", err);
      setLoading(false);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return "Never";
    return new Date(ts).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="tenant-switcher__overlay" onClick={onClose}>
      <div className="tenant-switcher__modal" onClick={(e) => e.stopPropagation()}>
        <div className="tenant-switcher__header">
          <h2 className="tenant-switcher__title">Switch Organization</h2>
          <button className="tenant-switcher__close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="tenant-switcher__list">
          {tenants.length === 0 && (
            <div style={{ padding: 20, textAlign: "center", color: "#999", fontSize: 13 }}>
              No organizations found. Try syncing.
            </div>
          )}
          {tenants.map((tenant) => {
            const displayName = tenant.name && tenant.name !== tenant.code ? tenant.name : `Org ${tenant.code}`;
            const initials = displayName.slice(0, 2).toUpperCase();
            return (
              <div
                key={tenant.code}
                className={`tenant-switcher__item${tenant.code === activeTenantCode ? " tenant-switcher__item--active" : ""}`}
                onClick={() => !loading && handleSwitch(tenant.code)}
              >
                <div className="tenant-switcher__item-icon">
                  {initials}
                </div>
                <div className="tenant-switcher__item-info">
                  <div className="tenant-switcher__item-name">{displayName}</div>
                  <div className="tenant-switcher__item-meta">
                    Code: {tenant.code} &middot; Last used: {formatDate(tenant.lastUsedAt)}
                  </div>
                </div>
                {tenant.code === activeTenantCode && (
                  <span className="tenant-switcher__active-badge">Active</span>
                )}
              </div>
            );
          })}
        </div>

        {isProvisioned ? (
          <>
            <div className="tenant-switcher__managed-note">
              Organizations managed by your administrator
            </div>
            {syncMessage && (
              <div style={{
                padding: "6px 16px",
                fontSize: 12,
                color: syncMessage.type === "error" ? "#d32f2f" : "#2e7d32",
                background: syncMessage.type === "error" ? "#ffeaea" : "#edf7ed",
                borderRadius: 4,
                margin: "0 16px 8px",
              }}>
                {syncMessage.text}
              </div>
            )}
            <button
              className="tenant-switcher__add-btn"
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? "Syncing..." : "Sync Organizations"}
            </button>
          </>
        ) : (
          <button
            className="tenant-switcher__add-btn"
            onClick={() => {
              onClose();
              if (onAddNew) onAddNew();
            }}
          >
            + Add New Organization
          </button>
        )}
      </div>
    </div>
  );
};

export default TenantSwitcher;
